import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import LoadSheetViewer from "@/pages/LoadSheetViewer";
import LoginOnlyRoute from "@/components/LoginOnlyRoute";
import ReturnsInspection from "@/pages/ReturnsInspection";
import ImportValidation from "@/pages/ImportValidation";
import ReportView from "@/pages/ReportView";
import TasksPage from "@/pages/TasksPage";
import TaskDetail from "@/pages/TaskDetail";
import TaskPrint from "@/pages/TaskPrint";
import ShiftPrint from "@/pages/ShiftPrint";
import UsersPage from "@/pages/UsersPage";
import UserDetail from "@/pages/UserDetail";
import ProductsPage from "@/pages/ProductsPage";
import SubscriptionsDuePage from "@/pages/SubscriptionsDuePage";
import ProductDetail from "@/pages/ProductDetail";
import StockOverviewPage from "@/pages/stock/StockOverviewPage";
import StockByLocationPage from "@/pages/stock/StockByLocationPage";
import StockMovementsPage from "@/pages/stock/StockMovementsPage";
import SpaUnitsPage from "@/pages/stock/SpaUnitsPage";
import StockScanPage from "@/pages/stock/StockScanPage";
import StockCountPage from "@/pages/stock/StockCountPage";
import ProductRequestsPage from "@/pages/stock/ProductRequestsPage";
import ShortPicksPage from "@/pages/stock/ShortPicksPage";
import StockRequestsPage from "@/pages/stock/StockRequestsPage";
import WooStockPage from "@/pages/stock/WooStockPage";
import PlantTransportPage from "@/pages/PlantTransportPage";
import ForkliftChecksPage from "@/pages/ForkliftChecksPage";
import BillsPage from "@/pages/BillsPage";
import FinanceReportsPage from "@/pages/FinanceReportsPage";
import LiveTrackingPage from "@/pages/LiveTrackingPage";
import VehicleDetail from "@/pages/VehicleDetail";
import Scheduler from "@/pages/Scheduler";
import InvoicesPage from "@/pages/InvoicesPage";
import QuotesPage from "@/pages/QuotesPage";
import QuoteDetail from "@/pages/QuoteDetail";
import WebSalesPage from "@/pages/WebSalesPage";
import ShopPage from "@/pages/ShopPage";
import OrdersPage from "@/pages/OrdersPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetail from "@/pages/ProjectDetail";
import RunsPage from "@/pages/RunsPage";
import RunDetail from "@/pages/RunDetail";
import RunPrint from "@/pages/RunPrint";
import HotspotReport from "@/pages/HotspotReport";
import FormsPage from "@/pages/FormsPage";
import ProfilePage from "@/pages/ProfilePage";
import MyNotes from "@/pages/MyNotes";
import MyTasks from "@/pages/MyTasks";
import CommunicationsInbox from "@/pages/CommunicationsInbox";
import NotificationCentre from "@/pages/NotificationCentre";
import OpsBriefing from "@/pages/OpsBriefing";
import Marketplace from "@/pages/Marketplace";
import EbayPage from "@/pages/EbayPage";
import GoogleShopping from "@/pages/GoogleShopping";
import OnlineCatalogue from "@/pages/OnlineCatalogue";
import BundleMigrationPage, { RequireAdminOnly } from "@/pages/BundleMigrationPage";
import GoogleReadiness from "@/pages/GoogleReadiness";
import TedOpsCatalogue from "@/pages/TedOpsCatalogue";
import TedInsights from "@/pages/TedInsights";
import TedCommandCentre from "@/pages/TedCommandCentre";
import SalesIntelReview from "@/pages/SalesIntelReview";
import NotificationsAdmin from "@/pages/NotificationsAdmin";
import CommunicationsSettings from "@/pages/CommunicationsSettings";
import WhatsAppSettings from "@/pages/WhatsAppSettings";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import MediaLibrary from "@/pages/MediaLibrary";
import TrustpilotReviewsPage from "@/pages/TrustpilotReviewsPage";
import GoogleTrendsPage from "@/pages/GoogleTrendsPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import WebsiteAnalyticsPage from "@/pages/WebsiteAnalyticsPage";
import GaConfigurePage from "@/pages/GaConfigurePage";
import GoogleAdsPage from "@/pages/GoogleAdsPage";
import EnquiryOutcomesPage from "@/pages/EnquiryOutcomesPage";
import SearchConsolePage from "@/pages/SearchConsolePage";
import EmailMarketingLayout from "@/pages/email/EmailMarketingLayout";
import EmOverview from "@/pages/email/EmOverview";
import EmSubscribers from "@/pages/email/EmSubscribers";
import EmSubscriberDetail from "@/pages/email/EmSubscriberDetail";
import EmSettings from "@/pages/email/EmSettings";
import EmLists from "@/pages/email/EmLists";
import EmListDetail from "@/pages/email/EmListDetail";
import EmSegmentBuilder from "@/pages/email/EmSegmentBuilder";
import EmCampaigns from "@/pages/email/EmCampaigns";
import EmCampaignEditor from "@/pages/email/EmCampaignEditor";
import EmCampaignReport from "@/pages/email/EmCampaignReport";
import EmAnalytics from "@/pages/email/EmAnalytics";
import EmTemplates from "@/pages/email/EmTemplates";
import EmTemplateEditor from "@/pages/email/EmTemplateEditor";
import EmCampaignDesign from "@/pages/email/EmCampaignDesign";
import EmMailcoachImport from "@/pages/email/EmMailcoachImport";
import EmEligibility from "@/pages/email/EmEligibility";
import EmLaunchReadiness from "@/pages/email/EmLaunchReadiness";
import EmAutomations from "@/pages/email/EmAutomations";
import EmAutomationDetail from "@/pages/email/EmAutomationDetail";
import EmProductLinks from "@/pages/email/EmProductLinks";
import EmAutomationPerformance from "@/pages/email/EmAutomationPerformance";
import EmWooMatching from "@/pages/email/EmWooMatching";
import EmReadiness from "@/pages/email/EmReadiness";
import EmOpportunities from "@/pages/email/EmOpportunities";
import EmRequests from "@/pages/email/EmRequests";
import EmCatalogue from "@/pages/email/EmCatalogue";
import EmAutoOverview from "@/pages/email/EmAutoOverview";
import WooHistoryPage from "@/pages/WooHistoryPage";
import HolidaysPage from "@/pages/HolidaysPage";
import AbsencesPage from "@/pages/AbsencesPage";
import HrDocumentsPage from "@/pages/HrDocumentsPage";
import TicketsPage from "@/pages/TicketsPage";
import TimesheetsPage from "@/pages/TimesheetsPage";
import DatabasesPage from "@/pages/DatabasesPage";
import CrewsPage from "@/pages/CrewsPage";
import FinancePnL from "@/pages/FinancePnL";
import ExpensesPage from "@/pages/ExpensesPage";
import OverdueInvoicesPage from "@/pages/OverdueInvoicesPage";
import CustomerPortal from "@/pages/portal/CustomerPortal";
import WorkBank from "@/pages/WorkBank";
import DocsApp from "@/pages/docs/DocsPortal";
import EposHome from "@/pages/epos/EposHome";
import EposRegister from "@/pages/epos/EposRegister";
import EposSales from "@/pages/epos/EposSales";
import EposReports from "@/pages/epos/EposReports";
import EposSettings from "@/pages/epos/EposSettings";
import UsersRoles from "@/pages/UsersRoles";
import QuarantinePage from "@/pages/QuarantinePage";
import ActivityLog from "@/pages/ActivityLog";
import LogViewer from "@/pages/LogViewer";
import LoadBoard from "@/pages/LoadBoard";
import RebookDashboard from "@/pages/RebookDashboard";
import ShippingSettings from "@/pages/ShippingSettings";
import CompanySettings from "@/pages/CompanySettings";
import MapsDiagnostic from "@/pages/MapsDiagnostic";
import RequirePerm from "@/components/RequirePerm";
import RequireCatalogue from "@/components/RequireCatalogue";
import RuleGroupsPage from "@/pages/RuleGroupsPage";
import PlatformRoute from "@/components/PlatformRoute";
import PlatformLayout from "@/pages/platform/PlatformLayout";
import PlatformHealth from "@/pages/platform/PlatformHealth";
import MerchantRegistration from "@/pages/platform/MerchantRegistration";
import PlatformCompanies from "@/pages/platform/PlatformCompanies";
import PlatformCompanyDetail from "@/pages/platform/PlatformCompanyDetail";
import PlatformOnboarding from "@/pages/platform/PlatformOnboarding";
import PlatformOnboardingDetail from "@/pages/platform/PlatformOnboardingDetail";
import PlatformIntegrations from "@/pages/platform/PlatformIntegrations";
import PlatformUsers from "@/pages/platform/PlatformUsers";
import PlatformEvents from "@/pages/platform/PlatformEvents";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import PlatformWhatsApp from "@/pages/platform/PlatformWhatsApp";
import PlatformDataRequests from "@/pages/platform/PlatformDataRequests";
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";
import DataDeletion from "@/pages/public/DataDeletion";
import DeletionStatus from "@/pages/public/DeletionStatus";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/data-deletion/status" element={<DeletionStatus />} />
          <Route
            path="/loading/:id"
            element={
              <LoginOnlyRoute>
                <LoadSheetViewer />
              </LoginOnlyRoute>
            }
          />
          <Route path="/portal/demo" element={<CustomerPortal />} />
          <Route path="/docs/*" element={<DocsApp />} />
          <Route path="/maps-diagnostic" element={<MapsDiagnostic />} />
          <Route
            path="/platform"
            element={
              <PlatformRoute>
                <PlatformLayout />
              </PlatformRoute>
            }
          >
            <Route index element={<PlatformHealth />} />
            <Route path="companies" element={<PlatformCompanies />} />
            <Route path="companies/:id" element={<PlatformCompanyDetail />} />
            <Route path="onboarding" element={<PlatformOnboarding />} />
            <Route path="onboarding/:id" element={<PlatformOnboardingDetail />} />
            <Route path="onboarding/:id/integrations" element={<PlatformIntegrations />} />
            <Route path="users" element={<PlatformUsers />} />
            <Route path="events" element={<PlatformEvents />} />
            <Route path="audit" element={<PlatformAudit />} />
            <Route path="merchant-registration" element={<MerchantRegistration />} />
            <Route path="whatsapp" element={<PlatformWhatsApp />} />
            <Route path="data-requests" element={<PlatformDataRequests />} />
          </Route>
          <Route
            path="/tasks/:id/print"
            element={
              <ProtectedRoute>
                <TaskPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduler/shifts/:id/print"
            element={
              <ProtectedRoute>
                <ShiftPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/runs/:id/print"
            element={
              <ProtectedRoute>
                <RunPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/epos/register"
            element={
              <ProtectedRoute>
                <RequirePerm anyOf={["epos.sell", "epos.view"]}>
                  <EposRegister />
                </RequirePerm>
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<RequirePerm perm="dashboard.view"><Dashboard /></RequirePerm>} />
            <Route path="/imports" element={<RequirePerm perm="imports.manage"><ImportValidation /></RequirePerm>} />
            <Route path="/returns" element={<RequirePerm perm="mobile.returns.inspect"><ReturnsInspection /></RequirePerm>} />
            <Route path="/reports/:id" element={<ReportView />} />
            <Route path="/tasks" element={<RequirePerm anyOf={["orders.view", "operations.view"]}><TasksPage /></RequirePerm>} />
            <Route path="/tasks/:id" element={<RequirePerm anyOf={["orders.view", "operations.view"]}><TaskDetail /></RequirePerm>} />
            <Route path="/operations/rebook" element={<RequirePerm perm="operations.view"><RebookDashboard /></RequirePerm>} />
            <Route path="/operations/forklift-checks" element={<RequirePerm perm="operations.view"><ForkliftChecksPage /></RequirePerm>} />
            <Route path="/projects" element={<RequirePerm perm="projects.view"><ProjectsPage /></RequirePerm>} />
            <Route path="/projects/:id" element={<RequirePerm perm="projects.view"><ProjectDetail /></RequirePerm>} />
            <Route path="/runs" element={<RequirePerm perm="operations.view"><RunsPage /></RequirePerm>} />
            <Route path="/reports/hotspots" element={<RequirePerm perm="operations.view"><HotspotReport /></RequirePerm>} />
            <Route path="/runs/:id" element={<RequirePerm perm="operations.view"><RunDetail /></RequirePerm>} />
            <Route path="/users" element={<RequirePerm perm="customers.view"><UsersPage /></RequirePerm>} />
            <Route path="/notifications" element={<NotificationCentre />} />
            <Route path="/ops-briefing" element={<OpsBriefing />} />
            <Route path="/ted-insights" element={<TedInsights />} />
            <Route path="/ted" element={<TedCommandCentre />} />
            <Route path="/sales-intelligence" element={<SalesIntelReview />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/ebay" element={<EbayPage />} />
            <Route path="/google-shopping" element={<GoogleShopping />} />
            <Route path="/communications/inbox" element={<RequirePerm anyOf={["communications.view", "communications.manage"]}><CommunicationsInbox /></RequirePerm>} />
            <Route path="/communications/settings" element={<RequirePerm perm="communications.providers"><CommunicationsSettings /></RequirePerm>} />
            <Route path="/communications/whatsapp-inbox" element={<RequirePerm anyOf={["whatsapp.view"]}><WhatsAppInbox /></RequirePerm>} />
            <Route path="/communications/whatsapp" element={<RequirePerm anyOf={["whatsapp.view", "whatsapp.settings.manage"]}><WhatsAppSettings key="main" /></RequirePerm>} />
            <Route path="/data/whatsapp" element={<RequirePerm anyOf={["whatsapp.view", "whatsapp.settings.manage"]}><WhatsAppSettings key="data" section="data" /></RequirePerm>} />
            <Route path="/data/email-launch" element={<RequirePerm anyOf={["email_marketing.view", "email_marketing.manage"]}><div className="p-6 lg:p-8"><EmLaunchReadiness /></div></RequirePerm>} />
            <Route path="/media-library" element={<RequirePerm anyOf={["orders.view", "forms.manage"]}><MediaLibrary /></RequirePerm>} />
            <Route path="/reviews" element={<RequirePerm perm="customers.view"><TrustpilotReviewsPage /></RequirePerm>} />
            <Route path="/marketing/trends" element={<RequirePerm perm="customers.view"><GoogleTrendsPage /></RequirePerm>} />
            <Route path="/marketing/website-analytics" element={<RequirePerm anyOf={["customers.view", "settings.manage"]}><WebsiteAnalyticsPage /></RequirePerm>} />
            <Route path="/marketing/website-analytics/configure" element={<RequirePerm anyOf={["settings.manage"]}><GaConfigurePage /></RequirePerm>} />
            <Route path="/analytics/google-ads" element={<RequirePerm anyOf={["customers.view", "settings.manage"]}><GoogleAdsPage /></RequirePerm>} />
            <Route path="/analytics/enquiry-outcomes" element={<RequirePerm anyOf={["orders.view"]}><EnquiryOutcomesPage /></RequirePerm>} />
            <Route path="/analytics/search-console" element={<RequirePerm anyOf={["customers.view", "settings.manage"]}><SearchConsolePage /></RequirePerm>} />
            <Route path="/email-marketing" element={<RequirePerm anyOf={["email_marketing.view", "email_marketing.manage"]}><EmailMarketingLayout /></RequirePerm>}>
              <Route index element={<EmOverview />} />
              <Route path="subscribers" element={<EmSubscribers />} />
              <Route path="eligibility" element={<EmEligibility />} />
              <Route path="launch" element={<EmLaunchReadiness />} />
              <Route path="subscribers/mailcoach-import" element={<RequirePerm anyOf={["email_marketing.manage"]}><EmMailcoachImport /></RequirePerm>} />
              <Route path="subscribers/:id" element={<EmSubscriberDetail />} />
              <Route path="settings" element={<EmSettings />} />
              <Route path="campaigns" element={<EmCampaigns />} />
              <Route path="campaigns/:id" element={<EmCampaignEditor />} />
              <Route path="campaigns/:id/report" element={<EmCampaignReport />} />
              <Route path="campaigns/:id/design" element={<EmCampaignDesign />} />
              <Route path="templates" element={<EmTemplates />} />
              <Route path="templates/:id" element={<EmTemplateEditor />} />
              <Route path="lists" element={<EmLists />} />
              <Route path="lists/:id" element={<EmListDetail />} />
              <Route path="lists/segments/new" element={<EmSegmentBuilder />} />
              <Route path="lists/segments/:id" element={<EmSegmentBuilder />} />
              <Route path="automations" element={<EmAutomations />} />
              <Route path="automations/links" element={<EmProductLinks />} />
              <Route path="automations/performance" element={<EmAutomationPerformance />} />
              <Route path="automations/woo-matching" element={<EmWooMatching />} />
              <Route path="automations/readiness" element={<EmReadiness />} />
              <Route path="automations/opportunities" element={<EmOpportunities />} />
              <Route path="automations/requests" element={<EmRequests />} />
              <Route path="automations/catalogue" element={<EmCatalogue />} />
              <Route path="automations/overview" element={<EmAutoOverview />} />
              <Route path="automations/:id" element={<EmAutomationDetail />} />
              <Route path="analytics" element={<EmAnalytics />} />
            </Route>
            <Route path="/settings/integrations" element={<RequirePerm anyOf={["settings.manage", "email_marketing.settings", "whatsapp.settings.manage"]}><IntegrationsPage /></RequirePerm>} />
            <Route path="/data/woocommerce-history" element={<RequirePerm perm="imports.manage"><WooHistoryPage /></RequirePerm>} />
            <Route path="/users/:id" element={<RequirePerm perm="customers.view"><UserDetail /></RequirePerm>} />
            <Route path="/products" element={<RequirePerm anyOf={["products.view", "stock.view"]}><ProductsPage /></RequirePerm>} />
            <Route path="/products/bundles" element={<RequirePerm anyOf={["products.view", "stock.view"]}><ProductsPage bundles /></RequirePerm>} />
            <Route path="/products/subscriptions" element={<RequirePerm anyOf={["products.view", "stock.view"]}><ProductsPage subscriptions /></RequirePerm>} />
            <Route path="/products/subscriptions/due" element={<RequirePerm anyOf={["products.view", "stock.view"]}><SubscriptionsDuePage /></RequirePerm>} />
            <Route path="/products/:id" element={<RequirePerm anyOf={["products.view", "stock.view"]}><ProductDetail /></RequirePerm>} />
            <Route path="/products/online-catalogue" element={<RequireCatalogue><OnlineCatalogue /></RequireCatalogue>} />
            <Route path="/products/bundle-migration" element={<RequireAdminOnly><BundleMigrationPage /></RequireAdminOnly>} />
            <Route path="/products/catalogue" element={<RequireCatalogue><TedOpsCatalogue /></RequireCatalogue>} />
            <Route path="/products/google-readiness" element={<RequireCatalogue><GoogleReadiness /></RequireCatalogue>} />
            <Route path="/stock" element={<RequirePerm perm="stock.view"><StockOverviewPage /></RequirePerm>} />
            <Route path="/stock/locations" element={<RequirePerm perm="stock.view"><StockByLocationPage /></RequirePerm>} />
            <Route path="/stock/movements" element={<RequirePerm perm="stock.view"><StockMovementsPage /></RequirePerm>} />
            <Route path="/stock/spa-units" element={<RequirePerm perm="stock.view"><SpaUnitsPage /></RequirePerm>} />
            <Route path="/stock/scan" element={<RequirePerm perm="stock.view"><StockScanPage /></RequirePerm>} />
            <Route path="/stock/count" element={<RequirePerm perm="stock.view"><StockCountPage /></RequirePerm>} />
            <Route path="/stock/product-requests" element={<RequirePerm anyOf={["stock.view", "products.view", "operations.view", "orders.view"]}><ProductRequestsPage /></RequirePerm>} />
            <Route path="/stock/short-picks" element={<RequirePerm anyOf={["stock.view", "operations.view", "orders.view"]}><ShortPicksPage /></RequirePerm>} />
            <Route path="/stock/requests" element={<RequirePerm anyOf={["stock.view", "operations.view"]}><StockRequestsPage /></RequirePerm>} />
            <Route path="/stock/woocommerce" element={<RequirePerm anyOf={["stock.view", "orders.view"]}><WooStockPage /></RequirePerm>} />
            <Route path="/orders" element={<RequirePerm perm="orders.view"><OrdersPage /></RequirePerm>} />
            <Route path="/load-board" element={<RequirePerm anyOf={["mobile.warehouse.movements", "deliveries.view", "operations.view"]}><LoadBoard /></RequirePerm>} />
            <Route path="/plant-transport" element={<RequirePerm anyOf={["deliveries.view", "operations.view"]}><PlantTransportPage /></RequirePerm>} />
            <Route path="/live-tracking" element={<RequirePerm anyOf={["deliveries.view", "operations.view"]}><LiveTrackingPage /></RequirePerm>} />
            <Route path="/plant-transport/:id" element={<RequirePerm anyOf={["deliveries.view", "operations.view"]}><VehicleDetail /></RequirePerm>} />
            <Route path="/job-pool" element={<RequirePerm perm="work_bank.view"><WorkBank /></RequirePerm>} />
            <Route path="/work-bank" element={<Navigate to="/job-pool" replace />} />
            <Route path="/scheduler" element={<RequirePerm perm="operations.view"><Scheduler /></RequirePerm>} />
            <Route path="/quotes" element={<RequirePerm perm="quotes.view"><QuotesPage /></RequirePerm>} />
            <Route path="/invoices" element={<RequirePerm perm="quotes.view"><InvoicesPage /></RequirePerm>} />
            <Route path="/quotes/:id" element={<RequirePerm perm="quotes.view"><QuoteDetail /></RequirePerm>} />
            <Route path="/forms" element={<RequirePerm perm="forms.view"><FormsPage /></RequirePerm>} />
            <Route path="/forms/rule-groups" element={<RequirePerm perm="forms.manage"><RuleGroupsPage /></RequirePerm>} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/me/notes" element={<MyNotes />} />
            <Route path="/me/tasks" element={<MyTasks />} />
            <Route path="/holidays" element={<RequirePerm perm="holidays.request"><HolidaysPage /></RequirePerm>} />
            <Route path="/absences" element={<RequirePerm perm="absences.view"><AbsencesPage /></RequirePerm>} />
            <Route path="/hr/documents" element={<HrDocumentsPage />} />
            <Route path="/tickets" element={<RequirePerm perm="tickets.create"><TicketsPage /></RequirePerm>} />
            <Route path="/timesheets" element={<RequirePerm perm="timesheets.view"><TimesheetsPage /></RequirePerm>} />
            <Route path="/web-sales" element={<RequirePerm perm="orders.view"><WebSalesPage /></RequirePerm>} />
            <Route path="/shop" element={<RequirePerm perm="orders.view"><ShopPage /></RequirePerm>} />
            <Route path="/settings/shipping" element={<RequirePerm anyOf={["settings.view", "settings.manage", "orders.view", "operations.view"]}><ShippingSettings /></RequirePerm>} />
            <Route path="/settings/company" element={<RequirePerm anyOf={["settings.manage"]}><CompanySettings /></RequirePerm>} />
            <Route path="/finance/pnl" element={<RequirePerm perm="finance.profit_loss.view"><FinancePnL /></RequirePerm>} />
            <Route path="/finance/expenses" element={<RequirePerm perm="expenses.view"><ExpensesPage /></RequirePerm>} />
            <Route path="/finance/bills" element={<RequirePerm anyOf={["finance.view", "expenses.view"]}><BillsPage /></RequirePerm>} />
            <Route path="/finance/reports" element={<RequirePerm anyOf={["finance.view", "expenses.view", "expenses.approve", "finance.profit_loss.view", "finance.manage"]}><FinanceReportsPage /></RequirePerm>} />
            <Route path="/finance/overdue-invoices" element={<RequirePerm anyOf={["finance.view", "quotes.view", "orders.view"]}><OverdueInvoicesPage /></RequirePerm>} />
            <Route path="/databases/:key" element={<RequirePerm perm="databases.view"><DatabasesPage /></RequirePerm>} />
            <Route path="/crews" element={<RequirePerm perm="databases.view"><CrewsPage /></RequirePerm>} />
            <Route path="/epos" element={<RequirePerm perm="epos.view"><EposHome /></RequirePerm>} />
            <Route path="/epos/sales" element={<RequirePerm anyOf={["epos.view", "epos.reports"]}><EposSales /></RequirePerm>} />
            <Route path="/epos/reports" element={<RequirePerm perm="epos.reports"><EposReports /></RequirePerm>} />
            <Route path="/epos/settings" element={<RequirePerm perm="epos.manage"><EposSettings /></RequirePerm>} />
            <Route path="/admin/notifications" element={<RequirePerm perm="users.manage"><NotificationsAdmin /></RequirePerm>} />
            <Route path="/admin/users-roles" element={<RequirePerm perm="users.manage"><UsersRoles /></RequirePerm>} />
            <Route path="/admin/activity-log" element={<RequirePerm anyOf={["audit.view", "audit.view_finance"]}><ActivityLog /></RequirePerm>} />
            <Route path="/admin/log-viewer" element={<LogViewer />} />
            <Route path="/admin/quarantine" element={<RequirePerm superOnly><QuarantinePage /></RequirePerm>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
